/*
This file is located in the main branch
Last time the file was updated (UTC): [23.07.2024 10:30:04]
Permissions to update outside the main branch: [false]
*/

import {
    NoData,
    PendingContainer,
    Tree,
    TreeNodeType,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { getRolesByParentId } from 'axios/roleApi';
import useApi from 'hooks/useApi';
import { RoleTableItem } from 'models/api/roles';
import { useMemo, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { RoleChangeModalFromType, RoleModalFromType } from './RoleChangeModal';
import { useRoleWorkplaceState } from './Roles';

import './RoleChangeModalTabRoles.scss';

const RoleChangeModalTabRoles = ({
    formHasRole,
    showOnlyAssigned,
    isFetching,
    allRoles,
}: {
    formHasRole: () => boolean;
    showOnlyAssigned?: boolean;
    isFetching: boolean;
    allRoles: RoleModalFromType[];
}) => {
    const { selectedItem } = useRoleWorkplaceState();

    const roles = useMemo(
        () =>
            showOnlyAssigned
                ? allRoles.filter((i) => selectedItem?.roles?.find((r) => r.id === i.id) || false)
                : allRoles,
        [allRoles, showOnlyAssigned]
    );

    return (
        <PendingContainer isPending={isFetching}>
            {!roles.length ? (
                <NoData />
            ) : (
                <div className="role-change-modal-tab-roles__list">
                    {roles.map((role, i) => (
                        <RoleRow
                            role={role}
                            index={i}
                            editable={true}
                            formHasRole={formHasRole}
                            key={role.id}
                            odd={i % 2 === 0}
                        />
                    ))}
                </div>
            )}
        </PendingContainer>
    );
};

const RoleRow = ({
    formHasRole,
    editable = false,
    role,
    index,
    odd = false,
}: {
    formHasRole: () => boolean;
    role: RoleTableItem;
    index: number;
    editable?: boolean;
    odd?: boolean;
}) => {
    const childRow = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();
    const api = useApi();

    const roleToNode = (role: RoleTableItem) => {
        const title = t('BackOfficeRoleName' + role.name, { defaultValue: '' });

        return !role.isActive ? (
            <i className="text-secondary">
                {title} <Trans i18nKey="BackOfficeInactive" />
            </i>
        ) : (
            <span>{title}</span>
        );
    };

    const getChildrenFunc = (role: RoleTableItem) => () =>
        getRolesByParentId(api, role.id?.toString() || '').then((roles) =>
            roles.data.map(
                (r: RoleTableItem, i) =>
                    ({
                        id: r.id,
                        inner: <RoleRow role={r} index={i} formHasRole={formHasRole} />,
                        hasChildren: r.hasChildren,
                        getChildren: getChildrenFunc(r),
                    }) as TreeNodeType
            )
        );

    return (
        <div className={odd ? 'gray-role-row' : ''}>
            <Row key={role.id} className={role.isActive ? '' : ' text-secondary'}>
                {editable && (
                    <Col md="auto">
                        <ValidatedInput<RoleChangeModalFromType>
                            key={role.id}
                            id={`checkbox-${role.id}`}
                            type="checkbox"
                            name={`roles.${index}.enabled`}
                            disabled={!formHasRole()}
                        />
                    </Col>
                )}
                <Col>
                    <Tree
                        id={role.id}
                        inner={role.name}
                        hasChildren={role.hasChildren}
                        getChildren={getChildrenFunc(role)}
                        container={childRow}
                    />
                </Col>
                <Col>{roleToNode(role)}</Col>
            </Row>
            <Row>
                <Col>
                    <div className="px-5" ref={childRow} />
                </Col>
            </Row>
        </div>
    );
};

export default RoleChangeModalTabRoles;
